import {
   ConditionalFieldOptions,
   DropdownFieldUiStyleEnum,
   FieldSelectorFieldUiStyleEnum,
   FieldType,
   GeoShapeVariantType,
   DependentFields as GraphqlDependentFields,
   MultiSelectFieldUiStyleEnum,
   RelationFieldValueType
} from '@flowwai-frontend/bps-officer-web/graphql'
import {
   DependentFields,
   FieldSelectorFieldUiStyle,
   FloatFieldResponseType,
   PrefixConditionalOptions,
   SystemFieldEnum,
   UnionField,
   UnionFilterFieldType
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { customFieldsConstants } from '../constants/AnalyticsCustomFieldConstants'
import {
   convertGqlConditionalOptionTypesToLocalType,
   convertGqlFieldTagToFieldTagType,
   convertGqlGeoLocationVariantTypeToLocationVariantType
} from '../factories/fields'
import { convertRelationOverviewDetailsToRelationData } from '../factories/relation'
import { UnionAnalyticsFilterFieldModelType } from '../types/filtersTypes'
import { convertColorVariantEnumToColorEnum } from '../utils/EnumConversionUtils'

const transformDependentField = (
   fields: GraphqlDependentFields
): DependentFields => ({
   option: fields.option,
   fieldIds: fields.dependentFieldIds
})

const transformPrefixConditionalOptions = (
   conditionalOptions: ConditionalFieldOptions[]
): PrefixConditionalOptions[] =>
   conditionalOptions.map(eachConditionalOption => ({
      parentFieldId: eachConditionalOption.parentFieldId,
      conditionalOptions: eachConditionalOption.options.map(
         eachOptionConfig => ({
            parentPrefixOption: eachOptionConfig.parentFieldOption,
            dependentOptions: eachOptionConfig.options
         })
      )
   }))

export const convertToUnionFieldType = (
   fieldResponse: any //FIXME: convert this to Sales_Crm_Web_Common_ConfigurationField_FragmentFragment
): UnionField | null => {
   const {
      fieldId,
      gofId,
      name,
      dynamicLabel,
      description,
      helpText,
      helpTextType,
      tooltip,
      placeholderText,
      isRequired,
      permissionType,
      isDependent,
      systemFieldEnum,
      salesTemplateId,
      fieldType,
      linkedSourceFieldId,
      referenceId,
      iconId
   } = fieldResponse

   const commonField = {
      fieldId: fieldId,
      gofId: gofId,
      fieldType: fieldType, //NOTE: will change based on fieldType
      defaultValue: null, //NOTE: will change based on fieldType
      name: name,
      dynamicLabel,
      description: description,
      iconUrl: null, //NOTE: iconUrl isn't coming from back-end
      helpText: helpText,
      helpTextType,
      tooltip: tooltip,
      placeholder: placeholderText,
      required: isRequired,
      permission: permissionType, //TODO: remove this once we build,
      isDependent: isDependent,
      systemFieldEnum: systemFieldEnum,
      salesTemplateId: salesTemplateId,
      linkedSourceFieldId: linkedSourceFieldId,
      referenceId: referenceId,
      iconId,
      tags: fieldResponse?.tags?.length
         ? fieldResponse?.tags
              ?.map(convertGqlFieldTagToFieldTagType)
              .filter(Boolean)
         : []
   }

   switch (fieldResponse.__typename) {
      case 'IntegerField':
         return {
            ...commonField,
            fieldType: FieldType.Integer,
            defaultValue: fieldResponse.intDefaultValue,
            isFormulaEnabled: fieldResponse.isFormulaEnabled
         }

      case 'FloatField':
         return {
            ...commonField,
            fieldType: FieldType.Float,
            defaultValue: fieldResponse.floatDefaultValue as FloatFieldResponseType,
            isFormulaEnabled: fieldResponse.isFormulaEnabled
         }

      case 'EmailField':
         return {
            ...commonField,
            fieldType: FieldType.Email,
            defaultValue: fieldResponse.stringDefaultValue,
            regexErrorMessage: fieldResponse.regexErrorMessage,
            validationRegex: fieldResponse.validationRegex,
            isUnique: fieldResponse.isUnique
         }

      case 'FileUploaderField':
         return {
            ...commonField,
            fieldType: FieldType.FileUploader,
            defaultValue: fieldResponse.stringDefaultValue,
            allowedFormats: fieldResponse.allowedFormats,
            maxNumberOfFiles: fieldResponse.maxNumberOfFilesToUpload,
            eachFileSize: fieldResponse.eachFileMaxSizeInKb,
            canDownload: fieldResponse.canDownload
         }

      case 'LongTextField':
         return {
            ...commonField,
            fieldType: FieldType.LongText,
            defaultValue: fieldResponse.stringDefaultValue,
            isFormulaEnabled: fieldResponse.isFormulaEnabled,
            validationRegex: fieldResponse.validationRegex,
            regexErrorMessage: fieldResponse.regexErrorMessage
         }

      case 'PlainTextField': {
         const isSearchableField =
            systemFieldEnum === SystemFieldEnum.ContactAssignee ||
            systemFieldEnum === SystemFieldEnum.LeadAssignee ||
            systemFieldEnum === SystemFieldEnum.DealAssignee ||
            systemFieldEnum === SystemFieldEnum.TaskAssignee ||
            systemFieldEnum === SystemFieldEnum.ApplicationAssignee ||
            systemFieldEnum === SystemFieldEnum.CustomObjectAssignee ||
            systemFieldEnum === SystemFieldEnum.LeadStage ||
            systemFieldEnum === SystemFieldEnum.DealStage ||
            systemFieldEnum === SystemFieldEnum.CustomObjectStage ||
            systemFieldEnum === SystemFieldEnum.ApplicationStage ||
            systemFieldEnum === SystemFieldEnum.ApplicationDisplayStage ||
            fieldId === customFieldsConstants.taskTemplateId ||
            fieldId === customFieldsConstants.activityTemplateId

         if (isSearchableField) {
            return {
               ...commonField,
               fieldType: 'SEARCHABLE',
               defaultValue: fieldResponse.stringDefaultValue
            }
         }

         return {
            ...commonField,
            fieldType: FieldType.PlainText,
            defaultValue: fieldResponse.stringDefaultValue,
            regexErrorMessage: fieldResponse.regexErrorMessage,
            validationRegex: fieldResponse.validationRegex,
            isUnique: fieldResponse.isUnique,
            isIdentificationField: fieldResponse.isIdentificationField,
            isFormulaEnabled: fieldResponse.isFormulaEnabled
         }
      }

      case 'ShortTextField': {
         return {
            ...commonField,
            fieldType: FieldType.ShortText,
            defaultValue: fieldResponse.stringDefaultValue,
            regexErrorMessage: fieldResponse.regexErrorMessage,
            validationRegex: fieldResponse.validationRegex,
            prefixOptions: fieldResponse.prefixOptions?.values ?? [],
            suffixOptions: fieldResponse.suffixOptions?.values ?? [],
            isUnique: fieldResponse.isUnique,
            isIdentificationField: fieldResponse.isIdentificationField,
            isFormulaEnabled: fieldResponse.isFormulaEnabled,
            prefixConditionalOptions: transformPrefixConditionalOptions(
               fieldResponse.prefixOptions?.conditionalPrefixOptions ?? []
            )
         }
      }

      case 'URLField':
         return {
            ...commonField,
            fieldType: FieldType.Url,
            defaultValue: fieldResponse.stringDefaultValue,
            regexErrorMessage: fieldResponse.regexErrorMessage,
            validationRegex: fieldResponse.validationRegex,
            alternateDisplayText: fieldResponse.alternateDisplayText
         }

      case 'TimeField':
         return {
            ...commonField,
            fieldType: FieldType.Time,
            defaultValue: fieldResponse.timeFieldDefaultValue
         }

      case 'DateField':
         return {
            ...commonField,
            fieldType: FieldType.Date,
            defaultValue: null
         }

      case 'DateTimeField':
         return {
            ...commonField,
            fieldType: FieldType.DateTime,
            defaultValue: null
         }

      case 'DropdownField': {
         const isRadioFieldUIStyle =
            fieldResponse.dropdownFieldUiStyle ===
            DropdownFieldUiStyleEnum.RadioGroup

         if (isRadioFieldUIStyle) {
            return {
               ...commonField,
               fieldType: FieldType.RadioGroup,
               defaultValue: fieldResponse.stringDefaultValue,
               options: fieldResponse.options
            }
         }

         return {
            ...commonField,
            fieldType: FieldType.Dropdown,
            defaultValue: fieldResponse.stringDefaultValue,
            options: fieldResponse.options,
            displayType: fieldResponse.isLabelsEnabled ? 'LABEL' : 'DROPDOWN',
            optionColors: fieldResponse.optionColors?.map((option: any) => ({
               option: option.option,
               color: convertColorVariantEnumToColorEnum(option.color)
            })),
            optionsValueType: convertGqlConditionalOptionTypesToLocalType(
               fieldResponse.optionsValueType
            ),
            conditionalOptionsParentFieldId: fieldResponse.conditionalOptions
               ?.length
               ? fieldResponse.conditionalOptions[0].parentFieldId
               : undefined,
            allowOtherOptions: fieldResponse?.allowOtherOptions
         }
      }

      case 'MultiSelectField': {
         const isCheckboxFieldUIStyle =
            fieldResponse.multiSelectFieldUiStyle ===
            MultiSelectFieldUiStyleEnum.CheckboxGroup

         if (isCheckboxFieldUIStyle) {
            return {
               ...commonField,
               fieldType: FieldType.CheckboxGroup,
               defaultValue: fieldResponse.multiStringDefaultValue,
               options: fieldResponse.options
            }
         }

         return {
            ...commonField,
            fieldType: FieldType.MultiSelect,
            defaultValue: fieldResponse.multiStringDefaultValue,
            options: fieldResponse.options,
            displayType: fieldResponse.isLabelsEnabled ? 'LABEL' : 'DROPDOWN',
            optionColors: fieldResponse.optionColors?.map((option: any) => ({
               option: option.option,
               color: convertColorVariantEnumToColorEnum(option.color)
            })),
            optionsValueType: convertGqlConditionalOptionTypesToLocalType(
               fieldResponse.optionsValueType
            ),
            conditionalOptionsParentFieldId: fieldResponse.conditionalOptions
               ?.length
               ? fieldResponse.conditionalOptions[0].parentFieldId
               : undefined,
            allowOtherOptions: fieldResponse?.allowOtherOptions
         }
      }
      case 'CheckboxGroupField':
         return {
            ...commonField,
            fieldType: FieldType.CheckboxGroup,
            defaultValue: fieldResponse.multiStringDefaultValue,
            options: fieldResponse.options
         }

      case 'RadioGroupField':
         return {
            ...commonField,
            fieldType: FieldType.RadioGroup,
            defaultValue: fieldResponse.stringDefaultValue,
            options: fieldResponse.options
         }

      case 'PhoneNumberField':
         return {
            ...commonField,
            fieldType: FieldType.PhoneNumber,
            defaultValue: null,
            isUnique: fieldResponse.isUnique,
            isIdentificationField: fieldResponse.isIdentificationField
         }

      case 'FieldsSelectorField': {
         const uiStyle = convertGqlFieldSelectorFieldUiStyleToFieldSelectorFieldUiStyle(
            fieldResponse.fieldsSelectorFieldUiStyle
         )

         return {
            ...commonField,
            fieldType: FieldType.FieldsSelector,
            defaultValue: fieldResponse.fieldSelectorFieldDefaultValue,
            options: fieldResponse.options,
            uiStyle,
            dependentFields: fieldResponse.dependentFields
               ? fieldResponse.dependentFields.map(
                    (field: GraphqlDependentFields) =>
                       transformDependentField(field)
                 )
               : [] //TODO: Need to validate this properly
         }
      }
      case 'RelationField': {
         const isMultiSelect =
            fieldResponse.valueType === RelationFieldValueType.Multi

         return {
            ...commonField,
            fieldType: FieldType.Relation,
            relation: convertRelationOverviewDetailsToRelationData(
               fieldResponse.relation
            ),
            secondDegreeRelation: fieldResponse.secondDegreeRelation
               ? convertRelationOverviewDetailsToRelationData(
                    fieldResponse.secondDegreeRelation
                 )
               : undefined,
            isMultiSelect
         }
      }
      case 'GeoLocationField': {
         return {
            ...commonField,
            fieldType: FieldType.GeoLocation,
            addressFieldIds: fieldResponse.addressFieldIds
         }
      }
      case 'GeoShapeField': {
         return {
            ...commonField,
            fieldType: FieldType.GeoShape,
            locationVariant: convertGqlGeoLocationVariantTypeToLocationVariantType(
               fieldResponse.variantType as GeoShapeVariantType
            ),
            addressFieldIds: fieldResponse.addressFieldIds
         }
      }

      default:
         return null
   }
}

export const convertUnionFieldModelToUnionFilterFieldType = (
   field: UnionAnalyticsFilterFieldModelType
): UnionFilterFieldType | null => {
   const {
      fieldId,
      gof,
      name,
      dynamicLabel,
      description,
      helpText,
      helpTextType,
      tooltip,
      placeholder,
      required,
      permission,
      isDependent,
      systemFieldEnum,
      templateId,
      fieldType,
      dataSource
   } = field

   const commonField = {
      fieldId: fieldId,
      gofId: gof?.gofId || null,
      fieldType: fieldType,
      defaultValue: null,
      name: name,
      dynamicLabel,
      description: description,
      iconUrl: null,
      helpText: helpText,
      helpTextType,
      tooltip: tooltip,
      placeholder,
      required,
      permission,
      isDependent: isDependent,
      systemFieldEnum: systemFieldEnum,
      salesTemplateId: templateId,
      dataSource
   }

   switch (field.fieldType) {
      case 'PLAIN_TEXT': {
         return {
            ...commonField,
            fieldType: 'PLAIN_TEXT'
         }
      }
      case 'SHORT_TEXT': {
         return {
            ...commonField,
            fieldType: 'SHORT_TEXT'
         }
      }

      case 'SEARCHABLE': {
         return {
            ...commonField,
            fieldType: 'SEARCHABLE'
         }
      }

      case 'INTEGER':
         return {
            ...commonField,
            fieldType: 'INTEGER'
         }

      case 'FLOAT':
         return {
            ...commonField,
            fieldType: 'FLOAT'
         }

      case 'EMAIL':
         return {
            ...commonField,
            fieldType: 'EMAIL'
         }

      case 'LONG_TEXT':
         return {
            ...commonField,
            fieldType: 'LONG_TEXT',
            validationRegex: field.validationRegex
         }

      case 'URL':
         return {
            ...commonField,
            fieldType: 'URL'
         }

      case 'TIME':
         return {
            ...commonField,
            fieldType: 'TIME'
         }

      case 'DATE':
         return {
            ...commonField,
            fieldType: 'DATE',
            minDate: field.minDate,
            maxDate: field.maxDate
         }

      case 'DATE_TIME':
         return {
            ...commonField,
            fieldType: 'DATE_TIME',
            minDate: field.minDate,
            maxDate: field.maxDate
         }

      case 'DROPDOWN': {
         const isLabelDisplayType = field.displayType === 'LABEL'

         if (isLabelDisplayType) {
            return {
               ...commonField,
               fieldType: 'DROPDOWN',
               options: field.options,
               displayType: 'LABEL',
               optionColors: field.optionColors ?? []
            }
         }

         return {
            ...commonField,
            fieldType: 'DROPDOWN',
            options: field.options,
            displayType: 'DROPDOWN'
         }
      }
      case 'MULTI_SELECT': {
         const isLabelDisplayType = field.displayType === 'LABEL'

         if (isLabelDisplayType) {
            return {
               ...commonField,
               fieldType: 'MULTI_SELECT',
               options: field.options,
               displayType: 'LABEL',
               optionColors: field.optionColors ?? []
            }
         }

         return {
            ...commonField,
            fieldType: 'MULTI_SELECT',
            options: field.options,
            displayType: 'DROPDOWN'
         }
      }

      case 'CHECKBOX_GROUP':
         return {
            ...commonField,
            fieldType: 'CHECKBOX_GROUP',
            options: field.options
         }

      case 'RADIO_GROUP':
         return {
            ...commonField,
            fieldType: 'RADIO_GROUP',
            options: field.options
         }

      case 'PHONE_NUMBER':
         return {
            ...commonField,
            fieldType: 'PHONE_NUMBER'
         }

      case 'FIELDS_SELECTOR':
         return {
            ...commonField,
            fieldType: 'FIELDS_SELECTOR',
            options: field.options
         }

      case 'GEO_LOCATION':
         return {
            ...commonField,
            fieldType: 'GEO_LOCATION'
         }
      case 'GEO_SHAPE':
         return {
            ...commonField,
            fieldType: 'GEO_SHAPE'
         }

      default:
         return null
   }
}

export const convertGqlFieldSelectorFieldUiStyleToFieldSelectorFieldUiStyle = (
   uiStyle: FieldSelectorFieldUiStyleEnum
): FieldSelectorFieldUiStyle => {
   switch (uiStyle) {
      case FieldSelectorFieldUiStyleEnum.Dropdown:
         return 'DROPDOWN'
      case FieldSelectorFieldUiStyleEnum.RadioGroup:
         return 'RADIO'
   }
}
